import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, take } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { LoanService } from '../loan/loan.service';
import { ApiService } from '../api/api.service';
import { Branch, GetZipCodeRequestParameters } from './branch.model';
import { setHttpParams } from 'src/app/helpers/http-params.helper';
import handleError from 'src/app/helpers/error-handler.helper';
import { ErrorMessageService } from '../error-message/error-message.service';

@Injectable({
  providedIn: 'root',
})
export class BranchService {
  branches = new BehaviorSubject<Array<Branch>>([]);
  selectedBranch = new BehaviorSubject<Branch | null>(null);
  showInfoButton = new BehaviorSubject<boolean>(false);
  loading = new BehaviorSubject<boolean>(false);

  constructor(
    private loanService: LoanService,
    private api: ApiService,
    private http: HttpClient,
    private errorMessageService: ErrorMessageService
  ) {}

  /**
   * GET request to get branch list by zipcode.
   *
   * @param params All params are required. If searching, set {domainId} to `1` as a default.
   */
  getBranchesBySearch(params: GetZipCodeRequestParameters) {
    const queryStringParams: HttpParams = setHttpParams(params);
    const endpoint = `${this.api.baseUrl}loan/location`;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    this.loading.next(true);
    this.errorMessageService.clearErrorMessages();

    this.http
      .get<Array<Branch>>(endpoint, {
        headers,
        params: queryStringParams,
      })
      .pipe(
        take(1),
        map((branches: Array<Branch>) => {
          return branches.map((branch: Branch) => {
            return {
              ...branch,
              Selected: false,
            };
          });
        }),
        catchError(handleError)
      )
      .subscribe({
        next: (branches) => {
          this.loading.next(false);
          this.branches.next(branches);
        },
        error: (error: any) => {
          this.loading.next(false);
          this.errorMessageService.addToErrorMessages(error.message);
        },
      });
  }

  /**
   * This clears the following:
   * * The branch results list.
   * * The selected branch.
   * * The `DomainID` on the LoanApplication.
   */
  clearBranchesOnSearch() {
    this.branches.next([]);
    this.selectedBranch.next(null);
    this.loanService.setBranchSelection(null);
  }

  /**
   * Sets the selected branch info; used in the navbar, and updates the LoanApplication by setting
   * the `DomainID`.
   *
   * @param branch The selected Branch object.
   */
  setBranch(branch: Branch | null) {
    this.selectedBranch.next(branch);
    this.loanService.setBranchSelection(branch?.DomainID ?? null);
  }

  /**
   * Sets the visible state of branch info located in the navbar.
   *
   * The 'Finish Application by Phone' button should only be displayed after the user
   * has completed the Applicant Info section.
   *
   * Use the constructor to set this state.
   *
   * @param showBranchInfo Show selected branch info in navbar.
   */
  setShowInfoButton(showBranchInfo: boolean) {
    this.showInfoButton.next(showBranchInfo);
  }
}
